import { LitElement, html, css } from 'lit'

export class ScarcityIndicator extends LitElement {
  static styles = css`
    @media (min-width: 800px) {
      .pdp-atf-gradient .md\\:tw-gap-10 {
        gap: 1.5rem;
      }
    }

    .pdp-scarcity-low-stock {
      display: flex;
      gap: 9px;
      align-items: center;
      justify-content: flex-start;
      font-size: 20px;
      font-style: normal;
      font-weight: 300;
      line-height: 18px; /* 90% */
      letter-spacing: -0.2px;
      color: #000;
    }

    .pdp-scarcity-percent-new {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 15px 12px;
      border-radius: 12px;
      border: 1px solid #e4e4e4;
      background: #fff;
      margin-bottom: 8px;
    }

    .pdp-scarcity-percent-progress-new {
      color: #000;
      font-size: 16px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .pdp-scarcity-percent-progress-new > div {
      border-radius: 1000px;
      background: #cdcdcd;
      overflow: hidden;
      height: 11px;
      flex: 1;
    }

    .pdp-scarcity-percent-progress-new > div > span {
      background: #e71d06;
      height: 100%;
      display: block;
    }

    .pdp-scarcity-low-stock-info {
      font-size: 14px;
      font-style: normal;
      font-weight: 200;
      line-height: 20px;
      color: #1e262a;
    }

    @media (min-width: 800px) {
      .pdp-scarcity-percent {
        flex: 1;
      }
    }

    #percentage-sold-text {
      margin-right: -10px
    }
  `

  static properties = {
    productId: { type: Number, attribute: 'product-id' },
    percentIncrement: { type: Number },
    minStartPercent: { type: Number },
    maxStartPercent: { type: Number },
    maxOffsetDays: { type: Number },
    baseDate: { type: String },
    currentPercent: { type: Number, state: true },
  }

  constructor() {
    super()
    this.percentIncrement = 1 // Global percent increment
    this.minStartPercent = 50 // Global min start percent
    this.maxStartPercent = 70 // Global max start percent
    this.maxOffsetDays = 30 // Max offset days for divergence
    this.baseDate = '2024-01-01T00:00:00Z' // Global base date
    this.currentPercent = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this.calculatePercentage()
  }

  generateSeedFromProductId(productId) {
    const str = productId.toString()
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const chr = str.charCodeAt(i)
      hash = (hash << 5) - hash + chr
      hash |= 0 // Convert to 32bit integer
    }
    return Math.abs(hash)
  }

  getRandomFromSeed(seed, min, max) {
    const x = Math.sin(seed) * 10000
    return min + (x - Math.floor(x)) * (max - min)
  }

  calculatePercentage() {
    if (!this.productId) {
      console.error('Product ID is required for ScarcityIndicator.')
      return
    }

    const seed = this.generateSeedFromProductId(this.productId)

    // Generate start percent per product
    const startPercent = this.getRandomFromSeed(seed, this.minStartPercent, this.maxStartPercent)

    // Calculate cycle length per product
    const cycleLength = Math.ceil((100 - startPercent) / this.percentIncrement)

    // Generate offset days per product
    const offsetSeed = this.generateSeedFromProductId(this.productId + '_offset')
    const offsetDays = Math.floor(this.getRandomFromSeed(offsetSeed, 0, this.maxOffsetDays))

    // Calculate start date per product
    const startDate = new Date(this.baseDate)
    startDate.setDate(startDate.getDate() + offsetDays)

    // Calculate current percentage
    const now = new Date()
    const totalDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24))
    const daysIntoCycle = totalDays % cycleLength
    let currentPercent = startPercent + daysIntoCycle * this.percentIncrement

    if (currentPercent > 100) {
      currentPercent = 100
    }

    this.currentPercent = Math.round(currentPercent)
    this.startPercent = Math.round(startPercent)
  }

  render() {
    return html`
      <div class="pdp-scarcity-percent-new">
        <div class="pdp-scarcity-low-stock">
          <!-- SVG Icon -->
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433283 8.00043 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 7.34783 18.9464 4.80429 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0ZM10 18.5714C8.30473 18.5714 6.64754 18.0687 5.23797 17.1269C3.82841 16.185 2.72979 14.8464 2.08104 13.2801C1.43229 11.7139 1.26254 9.99049 1.59327 8.3278C1.924 6.6651 2.74035 5.13782 3.93909 3.93908C5.13782 2.74035 6.66511 1.924 8.3278 1.59327C9.99049 1.26254 11.7139 1.43228 13.2801 2.08103C14.8464 2.72978 16.185 3.8284 17.1269 5.23797C18.0687 6.64753 18.5714 8.30473 18.5714 10C18.5714 12.2733 17.6684 14.4535 16.0609 16.0609C14.4535 17.6684 12.2733 18.5714 10 18.5714Z"
              fill="black"
            />
            <path
              d="M9.28585 4.28613H10.7144V12.1433H9.28585V4.28613ZM10.0001 14.2861C9.78823 14.2861 9.58108 14.349 9.40489 14.4667C9.22869 14.5844 9.09136 14.7518 9.01027 14.9475C8.92918 15.1433 8.90796 15.3587 8.9493 15.5666C8.99064 15.7744 9.09268 15.9653 9.24253 16.1152C9.39237 16.265 9.58328 16.3671 9.79111 16.4084C9.99895 16.4497 10.2144 16.4285 10.4102 16.3474C10.6059 16.2663 10.7733 16.129 10.891 15.9528C11.0087 15.7766 11.0716 15.5695 11.0716 15.3576C11.0716 15.0734 10.9587 14.8009 10.7578 14.5999C10.5568 14.399 10.2843 14.2861 10.0001 14.2861Z"
              fill="black"
            />
          </svg>
          <div>Low Stock</div>
        </div>
        <div class="pdp-scarcity-low-stock-info">This product has limited availability.</div>
        <div class="pdp-scarcity-percent-progress-new">
          <div
            role="progressbar"
            aria-valuenow="${this.currentPercent}"
            aria-valuemin="${this.startPercent}"
            aria-valuemax="100"
          >
            <span id="percentage-sold-bar" style="width: ${this.currentPercent}%;"></span>
          </div>
          <span id="percentage-sold-text">${this.currentPercent}</span>% Sold
        </div>
      </div>
    `
  }
}

customElements.define('scarcity-indicator', ScarcityIndicator)
